<template>
  <div class="animated-cursor">
    <div ref="cursorDotRef" class="cursor-dot"></div>
    <div ref="cursorOutlineRef" class="cursor-outline"></div>
  </div>
</template>

<script setup lang="ts">
import { ref, onMounted, onUnmounted } from 'vue'
import { gsap } from 'gsap'

const cursorDotRef = ref<HTMLElement | null>(null)
const cursorOutlineRef = ref<HTMLElement | null>(null)

let mouseX = 0
let mouseY = 0
let outlineX = 0
let outlineY = 0

const onMouseMove = (event: MouseEvent) => {
  mouseX = event.clientX
  mouseY = event.clientY

  gsap.to(cursorDotRef.value, {
    x: mouseX,
    y: mouseY,
    duration: 0.1,
    ease: 'power2.out'
  })
}

const animateOutline = () => {
  outlineX += (mouseX - outlineX) * 0.15
  outlineY += (mouseY - outlineY) * 0.15

  if (cursorOutlineRef.value) {
    gsap.set(cursorOutlineRef.value, {
      x: outlineX,
      y: outlineY
    })
  }

  requestAnimationFrame(animateOutline)
}

const onMouseEnterLink = () => {
  gsap.to(cursorDotRef.value, {
    scale: 1.5,
    duration: 0.3,
    ease: 'power2.out'
  })

  gsap.to(cursorOutlineRef.value, {
    scale: 1.5,
    duration: 0.5,
    ease: 'elastic.out(1, 0.3)'
  })
}

const onMouseLeaveLink = () => {
  gsap.to(cursorDotRef.value, {
    scale: 1,
    duration: 0.3,
    ease: 'power2.out'
  })

  gsap.to(cursorOutlineRef.value, {
    scale: 1,
    duration: 0.5,
    ease: 'elastic.out(1, 0.3)'
  })
}

onMounted(() => {
  window.addEventListener('mousemove', onMouseMove)
  animateOutline()

  // Add hover effects to interactive elements
  const interactiveElements = document.querySelectorAll('a, button, [role="button"]')
  interactiveElements.forEach(el => {
    el.addEventListener('mouseenter', onMouseEnterLink)
    el.addEventListener('mouseleave', onMouseLeaveLink)
  })
})

onUnmounted(() => {
  window.removeEventListener('mousemove', onMouseMove)

  const interactiveElements = document.querySelectorAll('a, button, [role="button"]')
  interactiveElements.forEach(el => {
    el.removeEventListener('mouseenter', onMouseEnterLink)
    el.removeEventListener('mouseleave', onMouseLeaveLink)
  })
})
</script>

<style scoped lang="scss">
.animated-cursor {
  position: fixed;
  top: 0;
  left: 0;
  pointer-events: none;
  z-index: 9999;

  @media (max-width: 768px) {
    display: none;
  }
}

.cursor-dot {
  position: absolute;
  width: 8px;
  height: 8px;
  background: var(--gradient-primary);
  border-radius: 50%;
  transform: translate(-50%, -50%);
  box-shadow: 0 0 10px rgba(102, 126, 234, 0.5);
  z-index: 10000;
}

.cursor-outline {
  position: absolute;
  width: 30px;
  height: 30px;
  border: 2px solid var(--primary-blue);
  border-radius: 50%;
  transform: translate(-50%, -50%);
  opacity: 0.5;
  z-index: 9999;
}
</style>
